// Utility functions for political leader data

// Calculate age from birth date
export const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
    }
    return age;
};

// Calculate tenure in years
export const calculateTenure = (startDate, endDate = null) => {
    if (!startDate) return null;
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();
    const years = end.getFullYear() - start.getFullYear();
    const monthDiff = end.getMonth() - start.getMonth();
    const dayDiff = end.getDate() - start.getDate();
    
    // Calculate precise tenure
    let tenure = years;
    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        tenure--;
    }
    
    // Return years and months for more precision
    const totalMonths = (end - start) / (1000 * 60 * 60 * 24 * 30.44); // Average month
    const months = Math.round(totalMonths % 12);
    
    return {
        years: tenure,
        months: months,
        totalDays: Math.floor((end - start) / (1000 * 60 * 60 * 24)),
        formatted: tenure > 0 ? `${tenure} year${tenure !== 1 ? 's' : ''}${months > 0 ? `, ${months} month${months !== 1 ? 's' : ''}` : ''}` : `${months} month${months !== 1 ? 's' : ''}`
    };
};

// Get generation label based on birth year or age
export const getGeneration = (birthDate) => {
    if (!birthDate) return 'Unknown';
    const birthYear = new Date(birthDate).getFullYear();
    
    if (birthYear >= 1997) return 'Gen Z';
    if (birthYear >= 1981) return 'Millennial';
    if (birthYear >= 1965) return 'Gen X';
    if (birthYear >= 1946) return 'Baby Boomer';
    if (birthYear >= 1928) return 'Silent Generation';
    return 'Greatest Generation';
};

// Get color coding for age display
export const getAgeColor = (age) => {
    if (!age) return '#9E9E9E'; // Gray for unknown
    if (age < 40) return '#4CAF50'; // Young - Green
    if (age < 50) return '#2196F3'; // Middle-aged - Blue  
    if (age < 65) return '#FF9800'; // Senior - Orange
    if (age < 75) return '#FF5722'; // Elder - Deep Orange
    return '#F44336'; // Very Elder - Red
};

// Get color coding for tenure display
export const getTenureColor = (years) => {
    if (!years) return '#9E9E9E'; // Gray for unknown
    if (years < 2) return '#81C784'; // New - Light Green
    if (years < 5) return '#4CAF50'; // Junior - Green
    if (years < 10) return '#2196F3'; // Experienced - Blue
    if (years < 15) return '#FF9800'; // Senior - Orange
    if (years < 20) return '#FF5722'; // Very Senior - Deep Orange
    return '#F44336'; // Extremely Senior - Red
};

// Format display for age with generation
export const formatAgeDisplay = (birthDate) => {
    const age = calculateAge(birthDate);
    const generation = getGeneration(birthDate);
    
    if (!age) return 'Age Unknown';
    return `${age} years old (${generation})`;
};

// Format tenure for display
export const formatTenureDisplay = (startDate, endDate = null) => {
    const tenure = calculateTenure(startDate, endDate);
    if (!tenure) return 'Tenure Unknown';
    return tenure.formatted;
};

// Calculate average age for a group
export const getAverageAge = (people) => {
    const validAges = people
        .map(person => {
            if (person.birthDate) return calculateAge(person.birthDate);
            if (person.age) return person.age;
            return null;
        })
        .filter(age => age !== null);
    
    if (validAges.length === 0) return null;
    const sum = validAges.reduce((total, age) => total + age, 0);
    return Math.round(sum / validAges.length * 10) / 10; // One decimal place
};

// Get generation breakdown for a group
export const getGenerationBreakdown = (people) => {
    const generations = {
        'Gen Z': 0,
        'Millennial': 0,
        'Gen X': 0,
        'Baby Boomer': 0,
        'Silent Generation': 0,
        'Greatest Generation': 0,
        'Unknown': 0
    };
    
    people.forEach(person => {
        const gen = person.birthDate ? getGeneration(person.birthDate) : 'Unknown';
        generations[gen]++;
    });
    
    return generations;
};

// Find oldest and youngest in a group
export const getAgeExtremes = (people) => {
    const peopleWithAge = people
        .map(person => ({
            ...person,
            currentAge: person.birthDate ? calculateAge(person.birthDate) : person.age
        }))
        .filter(person => person.currentAge)
        .sort((a, b) => b.currentAge - a.currentAge);
    
    if (peopleWithAge.length === 0) return { oldest: null, youngest: null };
    
    return {
        oldest: peopleWithAge[0],
        youngest: peopleWithAge[peopleWithAge.length - 1]
    };
};

// Compare ages between groups (e.g., Democrats vs Republicans)
export const compareAgesByParty = (people) => {
    const parties = {};
    
    people.forEach(person => {
        const party = person.party || 'Unknown';
        if (!parties[party]) {
            parties[party] = [];
        }
        parties[party].push(person);
    });
    
    const comparison = {};
    Object.keys(parties).forEach(party => {
        comparison[party] = {
            averageAge: getAverageAge(parties[party]),
            count: parties[party].length,
            extremes: getAgeExtremes(parties[party])
        };
    });
    
    return comparison;
};

// Get tenure statistics
export const getTenureStatistics = (officials) => {
    const tenures = officials
        .map(official => {
            if (official.termStart) {
                const tenure = calculateTenure(official.termStart, official.termEnd);
                return tenure ? tenure.years : null;
            }
            return null;
        })
        .filter(tenure => tenure !== null);
    
    if (tenures.length === 0) {
        return {
            average: null,
            longest: null,
            shortest: null,
            median: null
        };
    }
    
    tenures.sort((a, b) => a - b);
    const sum = tenures.reduce((total, tenure) => total + tenure, 0);
    const average = sum / tenures.length;
    const median = tenures.length % 2 === 0
        ? (tenures[tenures.length / 2 - 1] + tenures[tenures.length / 2]) / 2
        : tenures[Math.floor(tenures.length / 2)];
    
    return {
        average: Math.round(average * 10) / 10,
        longest: tenures[tenures.length - 1],
        shortest: tenures[0],
        median: Math.round(median * 10) / 10
    };
};